import * as v from "valibot";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import {
  CorasFooter,
  CorasLandingPage,
  CorasNavBar,
} from "@coras-io/web-components-react";
import {
  SupportedCurrencies,
  SupportedLocales,
  Sorting,
} from "@coras-io/web-components/types";
import { config, site } from "../config.ts";
import { handleParamsUpdate } from "../utils.ts";

const sortingOptions: Sorting = ["popularity", "lowest_price", "rating"];

const routeParamsSchema = v.object({
  locale: v.optional(v.picklist(config.locales), config.locale),
  currency: v.optional(v.picklist(config.currencies), config.currency),
});

const searchParamsSchema = v.object({
  city: v.optional(v.string()),
  country: v.optional(v.string()),
  startDate: v.optional(v.string()),
  endDate: v.optional(v.string()),
  sorting: v.optional(v.picklist(sortingOptions), "popularity"),
  categories: v.optional(v.string()),
  duration: v.optional(v.string()),
  price: v.optional(v.string()),
});

export const Route = createFileRoute("/$locale/$currency/")({
  component: LandingPageComponent,
  beforeLoad: ({ params }) => {
    if (!v.safeParse(routeParamsSchema, params).success) {
      throw redirect({
        replace: true,
        to: "/$locale/$currency",
        params: {
          locale: config.locale,
          currency: config.currency,
        },
      });
    }
  },
  params: { parse: (rawParams) => v.parse(routeParamsSchema, rawParams) },
  validateSearch: searchParamsSchema,
  head: () => {
    return {
      meta: [
        {
          title: site.title,
        },
        {
          name: "description",
          content: site.description,
        },
      ],
    };
  },
});

function LandingPageComponent() {
  const { locale, currency } = Route.useParams();
  const {
    city,
    country,
    startDate,
    endDate,
    sorting,
    categories,
    duration,
    price,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // @ts-ignore
  const onParamsUpdate = (params) => {
    return handleParamsUpdate(navigate, params);
  };

  const goToHomePage = async () => {
    await navigate({
      resetScroll: true,
      to: `/${locale}/${currency}`,
    });
  };

  return (
    <CorasLandingPage
      apiHost={import.meta.env.VITE_API_HOST}
      distributorID={import.meta.env.VITE_DISTRIBUTOR_ID}
      locales={config.locales}
      locale={locale as SupportedLocales}
      currencies={config.currencies}
      currency={currency as SupportedCurrencies}
      assetsURL={import.meta.env.VITE_ASSETS_URL}
      startDate={startDate || ""}
      endDate={endDate || ""}
      sorting={sorting}
      categories={categories}
      city={city || ""}
      country={country || ""}
      price={price}
      duration={duration}
      onParamsUpdate={onParamsUpdate}
    >
      <CorasNavBar slot="navbar">
        <img
          className="logo"
          src={site.logo}
          alt={site.title}
          slot="logo"
          onClick={goToHomePage}
        />
      </CorasNavBar>
      <CorasFooter slot="footer" />
    </CorasLandingPage>
  );
}
